<script setup>
import axios from 'axios';
import { computed, defineAsyncComponent, onBeforeUnmount, onMounted } from 'vue';
import { useAuthStore } from '../../stores/auth';
import { useSessionStore } from '../../stores/sessionStore';

/**
 * A thread's call, in the same LiveKit room component the chat's video calls
 * use: a synthetic "video chat" event whose title is the room the server
 * minted. Whoever started it hosts; the other end joins as a speaker.
 */
const Session = defineAsyncComponent(() => import('../../livekit-client/Session.vue'));

const props = defineProps({
  room: { type: String, required: true },
  host: { type: Boolean, default: false },
  leaveUrl: { type: String, default: '' },
});

const emit = defineEmits(['close']);

const auth = useAuthStore();
const sessionStore = useSessionStore();

const me = computed(() => ({ id: auth.userData?.subdomain_user_id }));

const event = computed(() => ({
  title: props.room,
  video_call_type: 'video_conference',
  original_start_date: new Date(Date.now() - 86400000).toISOString(),
  original_end_date: new Date(Date.now() + 86400000).toISOString(),
  isVideoChat: true,
  ...(props.host ? { session_moderator: me.value } : { session_speakers: [me.value] }),
}));

sessionStore.setIsVideoChat(true);

let left = false;

const close = async () => {
  if (left) return;
  left = true;
  if (props.leaveUrl) await axios.post(props.leaveUrl).catch(() => {});
  emit('close');
};

const leaveWithPage = () => {
  if (left || !props.leaveUrl) return;
  left = true;
  fetch(props.leaveUrl, {
    method: 'POST',
    keepalive: true,
    headers: { Accept: 'application/json', Authorization: axios.defaults.headers.common.Authorization },
  }).catch(() => {});
};

onMounted(() => window.addEventListener('pagehide', leaveWithPage));

onBeforeUnmount(() => {
  window.removeEventListener('pagehide', leaveWithPage);
  sessionStore.setIsVideoChat(false);
  sessionStore.reset();
});
</script>

<template>
  <div class="call-room" role="dialog" aria-modal="true" aria-label="Call" data-testid="call-room">
    <div class="call-room__bar">
      <span>{{ props.host ? 'Your call' : 'Call with your advisor' }}</span>
      <button type="button" data-testid="leave-call" @click="close">Leave the call</button>
    </div>
    <Session class="call-room__session" :event="event" @close="close" @disconnected="close" />
  </div>
</template>

<style scoped lang="scss">
.call-room {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  background: #1c1720;
  color: #fff;
}

.call-room__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  font-size: 14px;

  button {
    height: 34px;
    padding: 0 15px;
    border: 0;
    border-radius: 99px;
    background: #a32020;
    color: #fff;
    font: inherit;
    font-weight: 600;
    cursor: pointer;
  }
}

.call-room__session { flex: 1; min-height: 0; }
</style>
